/**
 * Session Management Utilities
 * Generate and manage analytics session IDs
 */

/** Session storage key */
const SESSION_ID_KEY = 'ufc-analytics-session-id'
const SESSION_START_KEY = 'ufc-analytics-session-start'

/** Session timeout in milliseconds (30 minutes) */
const SESSION_TIMEOUT_MS = 30 * 60 * 1000

/**
 * Generate a unique session ID
 * Format: timestamp-random
 */
export function generateSessionId(): string {
  const timestamp = Date.now().toString(36)
  const random = Math.random().toString(36).substring(2, 10)
  return `${timestamp}-${random}`
}

/**
 * Get or create a session ID
 * Creates a new session if:
 * - No session exists
 * - Session has expired (30 min inactivity)
 */
export function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') {
    return generateSessionId()
  }

  const stored = sessionStorage.getItem(SESSION_ID_KEY)
  const lastActivity = sessionStorage.getItem(SESSION_START_KEY)

  if (stored && lastActivity) {
    const elapsed = Date.now() - parseInt(lastActivity, 10)
    if (elapsed < SESSION_TIMEOUT_MS) {
      // Session still valid, update activity time
      sessionStorage.setItem(SESSION_START_KEY, Date.now().toString())
      return stored
    }
  }

  // Create new session
  const newSessionId = generateSessionId()
  sessionStorage.setItem(SESSION_ID_KEY, newSessionId)
  sessionStorage.setItem(SESSION_START_KEY, Date.now().toString())

  return newSessionId
}

/**
 * Clear the current session
 * Call this on logout
 */
export function clearSession(): void {
  if (typeof window === 'undefined') return

  sessionStorage.removeItem(SESSION_ID_KEY)
  sessionStorage.removeItem(SESSION_START_KEY)
}

/**
 * Touch session (update last activity time)
 */
export function touchSession(): void {
  if (typeof window === 'undefined') return

  sessionStorage.setItem(SESSION_START_KEY, Date.now().toString())
}

/**
 * Get session duration in milliseconds
 */
export function getSessionDuration(): number {
  if (typeof window === 'undefined') return 0

  const startTime = sessionStorage.getItem(SESSION_START_KEY)
  if (!startTime) return 0

  return Date.now() - parseInt(startTime, 10)
}

/**
 * Check if session is valid (not expired)
 */
export function isSessionValid(): boolean {
  if (typeof window === 'undefined') return false

  const lastActivity = sessionStorage.getItem(SESSION_START_KEY)
  if (!lastActivity) return false

  const elapsed = Date.now() - parseInt(lastActivity, 10)
  return elapsed < SESSION_TIMEOUT_MS
}
